/**
 * AppLayout - Desktop sidebar layout for Kantoor App
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import {
  useAuthStore,
  useWebshopStore,
  useWorkforceStore,
  useInboxStore,
  usePhoneStore,
  useBerichtenStore,
} from '../../stores';
import { useUserRole, herlaadRol, ROL_MISLUKT_DREMPEL } from '../../hooks/useUserRole';
import { useCommandPalette } from '../../hooks/useCommandPalette';
import { SidebarPhoneDock } from '../phone/containers/SidebarPhoneDock';
import { SlideInPhonePanel } from '../phone/containers/SlideInPhonePanel';
import { PHONE_DOCK_VARIANT } from '../phone/dock-variant';
import { AppointmentScheduler } from '../appointment-scheduler/AppointmentScheduler';
import { OffertePanel } from '../offerte-panel/OffertePanel';
import { WasserijItemDrawerPanel } from '../order-detail/WasserijItemDrawerPanel';
import { OrderDetailDrawerPanel } from '../order-detail/OrderDetailDrawerPanel';
import { OfferteDetailDrawerPanel } from '../offerte-detail/OfferteDetailDrawerPanel';
import { OpportunityDetailDrawerPanel } from '../opportunity-detail/OpportunityDetailDrawerPanel';
import { CallDetailDrawerPanel } from '../phone/CallDetailDrawerPanel';
import { InvoiceDetailDrawerPanel } from '../invoice-detail/InvoiceDetailDrawerPanel';
import { initTelephony, phoneState } from '../../lib/telephony';
import { setSentryUser } from '../../lib/telephony/sentry';
import { loadPreferences } from '../../lib/telephony/devices';
import { supabase } from '../../lib/supabase';
import { fetchKlachtCounts } from '../../lib/klachten';
import { fetchRisicoOpenCount } from '../../lib/risico';
import { fetchKasOpenVorigeDagen } from '../../lib/kas';
import { fetchReparatieIntentTodoCount, fetchReparatieOfferteTodoCount } from '../../lib/reparatie';
import { fetchOphaalReminderActieVereistCount } from '../../lib/ophaal-reminder';
import { fetchSocialWachtrijCount } from '../../lib/social';
import { salesforceClient } from '../../lib/salesforce';
import { LogoutIcon, SearchIcon } from './sidebar-icons';
import { SidebarSection } from './SidebarSection';
import { SidebarItem, type BadgeValues } from './SidebarItem';
import { CommandPalette } from './CommandPalette';
import { AppUpdateBanner } from './AppUpdateBanner';
import { filterNavigationByRole } from './navigation';
import { EXTERN_LOKET_HOME, isExternLoket, isExternLoketPath } from '../../lib/extern-loket';
import { startVersionWatch } from '../../lib/app-version';

function isItemActive(href: string, pathname: string): boolean {
  if (href === '/') return pathname === '/';
  return pathname === href || pathname.startsWith(`${href}/`);
}

/**
 * Layout-route (App.tsx): mount één keer per sessie, pagina's wisselen in de
 * <Outlet/>. Niet per pagina renderen — dan herstart elke navigatie de
 * badge-pollers en de telefonie-init en flitst de sidebar.
 */
export function AppLayout() {
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { role, isLoading: rolLaadt, mislukt: rolMislukt } = useUserRole();
  // Werkbank #99 (Albert 11-10): rol niet geladen = leeg menu zonder uitleg.
  // Na drie mislukte pogingen zeggen we het en bieden we Opnieuw laden.
  const rolOnbekend = role === null && rolMislukt >= ROL_MISLUKT_DREMPEL;
  // Het extern loket (callcenter) ziet alleen Nieuwe aanvraag, Prijzen en
  // Kennisbank; de badge-pollers hieronder zouden voor dat account alleen
  // 403's uit de middleware produceren. Rol nog onbekend telt als loket:
  // liever een paar seconden geen badges dan een poll die niet hoort.
  const loket = role === null || isExternLoket(role);
  const { orderCounts, startPolling, stopPolling } = useWebshopStore();
  const { pendingLeaveCount, fetchPendingCount } = useWorkforceStore();
  const {
    counts: berichtenCounts,
    startPolling: startBerichtenPolling,
    stopPolling: stopBerichtenPolling,
  } = useBerichtenStore();
  const {
    nieuwCount,
    mijnCount,
    startPolling: startInboxPolling,
    stopPolling: stopInboxPolling,
  } = useInboxStore();
  const { open: paletteOpen, setOpen: setPaletteOpen } = useCommandPalette();
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('office_sidebar_collapsed') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('office_sidebar_collapsed', sidebarCollapsed ? '1' : '0'); } catch { /* privémodus */ }
  }, [sidebarCollapsed]);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '\\') { e.preventDefault(); setSidebarCollapsed((v) => !v); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
  const [klachtenOpen, setKlachtenOpen] = useState<number>(0);
  const [declaratiesOpen, setDeclaratiesOpen] = useState<number>(0);
  const [risicosOpen, setRisicosOpen] = useState<number>(0);
  const [kasAchterstand, setKasAchterstand] = useState<number>(0);
  const [reparatiesToplannen, setReparatiesToplannen] = useState<number>(0);
  const [ophaalActieVereist, setOphaalActieVereist] = useState<number>(0);
  const [opvolgenOpen, setOpvolgenOpen] = useState<number>(0);
  const [signalenOpen, setSignalenOpen] = useState<number>(0);
  const [socialWachtrij, setSocialWachtrij] = useState<number>(0);
  const [routeVoorstellen, setRouteVoorstellen] = useState<number>(0);

  // Start webshop polling so badge stays current on all pages
  useEffect(() => {
    if (loket) return;
    startPolling();
    return () => stopPolling();
  }, [loket, startPolling, stopPolling]);

  // Berichten sidebar-badge polling (60s). Telt open berichten van uitvoering
  // die kantoor moet afhandelen.
  useEffect(() => {
    if (loket) return;
    startBerichtenPolling(60_000);
    return () => stopBerichtenPolling();
  }, [loket, startBerichtenPolling, stopBerichtenPolling]);

  // Start inbox polling (30s)
  useEffect(() => {
    if (loket) return;
    startInboxPolling(30000);
    return () => stopInboxPolling();
  }, [loket, startInboxPolling, stopInboxPolling]);

  // Initialise telephony client once per authenticated session. Niet voor het
  // extern loket: dat heeft geen Telnyx-credentials en zou anders in een
  // reconnect-lus op 403's van /api/telephony/token belanden.
  const telephonyStarted = useRef(false);
  useEffect(() => {
    if (role === null || isExternLoket(role) || telephonyStarted.current) return;
    telephonyStarted.current = true;
    let cancelled = false;
    (async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user || cancelled) return;
      setSentryUser(user.id);
      await initTelephony(user.id);
      const prefs = await loadPreferences();
      if (prefs && !cancelled) {
        usePhoneStore.getState().hydrateDevices({
          microphone: { id: prefs.microphoneId, label: prefs.microphoneLabel },
          speaker: { id: prefs.speakerId, label: prefs.speakerLabel },
          ringtoneSpeaker: { id: prefs.ringtoneSpeakerId, label: prefs.ringtoneSpeakerLabel },
          ringtoneVolume: prefs.ringtoneVolume,
        });
      }
    })();
    return () => {
      cancelled = true;
      // Client is tab-scoped; cleanup on beforeunload via lib/telephony/client.ts.
    };
  }, [role]);

  // Versiecheck: tabs leven dagen op één bundle; verborgen tabs zonder gesprek
  // herladen vanzelf, zichtbare krijgen de banner (lib/app-version.ts).
  useEffect(() => startVersionWatch(), []);

  // Mirror the cross-tab phone-state into the UI store. status/mute/disposition
  // all originate on the owner tab, so this works identically on every tab.
  useEffect(() => {
    const apply = () => {
      const store = usePhoneStore.getState();
      store.setStatus(phoneState.getStatus());
      store.setMuted(phoneState.getMuted());
      store.setDispositionCall(phoneState.getDisposition());
    };
    apply();
    return phoneState.subscribe(apply);
  }, []);

  useEffect(() => {
    if (role === 'admin') fetchPendingCount();
  }, [role, fetchPendingCount]);

  // Klacht-count voor sidebar-badge. 60s polling — bij volume <100 klachten
  // is een COUNT()-SOQL goedkoop genoeg; later eventueel cachen via Supabase
  // mirror. Zelfde rol-gating als de /klachten link.
  useEffect(() => {
    if (role !== 'admin' && role !== 'medewerker') return;
    let cancelled = false;
    const fetchOnce = () => {
      if (document.hidden) return; // geen SF-polling op verborgen/achtergrond-tabs
      fetchKlachtCounts()
        .then((counts) => {
          if (!cancelled) setKlachtenOpen(counts.open);
        })
        .catch(() => {
          // Stil falen — sidebar-badge is geen kritisch UI-element
        });
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 300_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [role]);

  // Declaratie-count voor sidebar-badge: ingediend + goedgekeurd (werkvoorraad
  // kantoor: keuren of overmaken). 5 min polling, zelfde rol-gating als /declaraties.
  useEffect(() => {
    if (role !== 'admin' && role !== 'medewerker') return;
    let cancelled = false;
    const fetchOnce = async () => {
      if (document.hidden) return;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const res = await fetch('/api/workforce/declaraties?status=open', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setDeclaratiesOpen(Array.isArray(data.declaraties) ? data.declaraties.length : 0);
      } catch {
        // Stil falen: sidebar-badge is geen kritisch UI-element
      }
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 300_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [role]);

  // Extra-rit-voorstellen voor de badge op Routes (plan 2026-10-28): open
  // live-concepten. 60s polling; de cron schrijft 1x per ochtend, maar een
  // klik van een collega moet de badge snel laten zakken. Flag uit = 0.
  useEffect(() => {
    if (role !== 'admin' && role !== 'medewerker') return;
    let cancelled = false;
    const fetchOnce = () => {
      if (document.hidden) return;
      salesforceClient
        .extraVoorstellen({ status: 'concept', modus: 'live' })
        .then((r) => {
          if (!cancelled) setRouteVoorstellen(r.modus === 'live' && r.magBeoordelen !== false ? r.voorstellen.length : 0);
        })
        .catch(() => {});
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [role]);

  // Social-wachtrij voor de sidebar-badge (22-10): wie keurt (admin, medewerker)
  // ziet hoeveel voorstellen wachten. 5 min polling; de Action maakt 1x per dag.
  useEffect(() => {
    if (role !== 'admin' && role !== 'medewerker') return;
    let cancelled = false;
    const fetchOnce = () => {
      if (document.hidden) return;
      fetchSocialWachtrijCount()
        .then((n) => {
          if (!cancelled) setSocialWachtrij(n);
        })
        .catch(() => {});
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 300_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [role]);

  // Risico-count voor sidebar-badge. 60s polling. Zelfde rol-gating als de
  // /risicos link.
  useEffect(() => {
    if (role !== 'admin' && role !== 'medewerker') return;
    let cancelled = false;
    const fetchOnce = () => {
      if (document.hidden) return; // geen SF-polling op verborgen/achtergrond-tabs
      fetchRisicoOpenCount()
        .then((count) => {
          if (!cancelled) setRisicosOpen(count);
        })
        .catch(() => {});
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 300_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [role]);

  // Kas-achterstand voor sidebar-badge. 60s polling. Telt openstaande
  // contante transacties van vóór vandaag (achterstand). Skip voor
  // callcenter — die ziet de Kas-link niet.
  useEffect(() => {
    if (role !== 'admin' && role !== 'medewerker') return;
    let cancelled = false;
    const fetchOnce = () => {
      if (document.hidden) return; // geen SF-polling op verborgen/achtergrond-tabs
      fetchKasOpenVorigeDagen()
        .then((count) => {
          if (!cancelled) setKasAchterstand(count);
        })
        .catch(() => {});
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 300_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [role]);

  // Reparaties-badge = te-plannen intents + ingediende offertes (te accorderen).
  // 60s polling. Zelfde rol-gating als de /reparaties link (admin + medewerker).
  useEffect(() => {
    if (role !== 'admin' && role !== 'medewerker') return;
    let cancelled = false;
    const fetchOnce = () => {
      if (document.hidden) return; // geen SF-polling op verborgen/achtergrond-tabs
      Promise.all([
        fetchReparatieIntentTodoCount().catch(() => 0),
        fetchReparatieOfferteTodoCount().catch(() => 0),
      ])
        .then(([intents, offertes]) => {
          if (!cancelled) setReparatiesToplannen(intents + offertes);
        })
        .catch(() => {});
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 300_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [role]);

  // Ophaal-reminder actie-vereist count voor sidebar-badge. 60s polling.
  // Zelfde rol-gating als de /orders link (admin + medewerker).
  useEffect(() => {
    if (role !== 'admin' && role !== 'medewerker') return;
    let cancelled = false;
    const fetchOnce = () => {
      if (document.hidden) return; // geen SF-polling op verborgen/achtergrond-tabs
      fetchOphaalReminderActieVereistCount()
        .then((count) => {
          if (!cancelled) setOphaalActieVereist(count);
        })
        .catch(() => {});
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 300_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [role]);

  // Twee gescheiden tellers uit hetzelfde taken-primitief: Telefoon telt alleen
  // belwerk (voicemail/gemist), Signalen alleen systeemmeldingen. Vroeger telde
  // de Telefoon-badge alles, waardoor callcenter op een getal jaagde dat deels
  // uit taken bestond die niet in hun tab stonden. Eén poll, twee counts.
  // 60s polling — Supabase-only (ops_tasks), geen SF-verbruik.
  useEffect(() => {
    if (loket) return;
    let cancelled = false;
    const fetchOnce = async () => {
      if (document.hidden) return;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const headers = { Authorization: `Bearer ${session.access_token}` };
        const [telefoon, systeem] = await Promise.all([
          fetch('/api/tasks?status=open&limit=1&kind=telefoon', { headers }),
          fetch('/api/tasks?status=open&limit=1&kind=systeem', { headers }),
        ]);
        if (telefoon.ok) {
          const json = await telefoon.json();
          if (!cancelled) setOpvolgenOpen(json.total ?? 0);
        }
        if (systeem.ok) {
          const json = await systeem.json();
          if (!cancelled) setSignalenOpen(json.total ?? 0);
        }
      } catch {
        // stil falen — sidebar-badge is geen kritisch UI-element
      }
    };
    void fetchOnce();
    const id = setInterval(() => void fetchOnce(), 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [loket]);

  const sections = useMemo(
    () => (role ? filterNavigationByRole(role) : []),
    [role]
  );

  const badges: BadgeValues = {
    inboxNew: nieuwCount,
    inboxMine: mijnCount,
    berichtenUnread: berichtenCounts.open,
    webshopPending: orderCounts.klaarmakenVerzending + orderCounts.ingepakt,
    kasAchterstand,
    risicosOpen,
    verlofPending: pendingLeaveCount,
    klachtenOpen,
    declaratiesOpen,
    reparatiesToplannen,
    ophaalActieVereist,
    opvolgenOpen,
    signalenOpen,
    socialWachtrij,
    routeVoorstellen,
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="h-screen flex bg-muted overflow-hidden">
      {/* Sidebar. Ingeklapt = breedte 0 maar wél gemonteerd: de softphone-dock
          leeft in deze kolom en mag niet unmounten (Willem 14-09: inbox als
          volledig scherm). Toggle: knop bij het logo, ⌘\ of de rand links. */}
      <aside className={`bg-slate-900 flex flex-col flex-shrink-0 overflow-hidden transition-[width] duration-150 ${sidebarCollapsed ? 'w-0' : 'w-sidebar'}`}>
        {/* Logo */}
        <div className="h-16 flex items-center px-5 border-b border-slate-700/50">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-9 h-9 bg-indigo-500 rounded-lg flex items-center justify-center shadow-lg flex-shrink-0">
              <span className="text-white font-bold text-lg">C</span>
            </div>
            <span className="text-white font-semibold text-lg tracking-tight truncate">Cinco Office</span>
          </div>
          <button
            type="button"
            onClick={() => setSidebarCollapsed(true)}
            title="Menu inklappen (⌘\)"
            className="text-slate-500 hover:text-white p-1 rounded transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
        </div>

        {/* Command palette trigger — bovenaan: zoeken is de meest gebruikte actie */}
        <div className="px-3 pt-3">
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 bg-slate-800/50 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <SearchIcon className="w-4 h-4" />
            <span className="flex-1 text-left">Zoeken</span>
            <span className="text-xs font-mono text-slate-500">⌘K</span>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-3 px-3 overflow-y-auto">
          {sections.map((section) => {
            const activeRoute = section.items.some((i) =>
              isItemActive(i.href, location.pathname)
            );
            return (
              <SidebarSection
                key={section.id}
                section={section}
                isActiveRoute={activeRoute}
              >
                {section.items.map((item) => (
                  <SidebarItem key={item.id} item={item} badges={badges} />
                ))}
              </SidebarSection>
            );
          })}
        </nav>

        {/* Docked softphone (sidebar variant) — slim bar above the user block */}
        {role !== 'callcenter' && PHONE_DOCK_VARIANT === 'sidebar' && <SidebarPhoneDock />}

        {/* User section */}
        <div className="p-3 border-t border-slate-700/50">
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-800 transition-colors">
            <div className="w-9 h-9 bg-indigo-600 rounded-full flex items-center justify-center ring-2 ring-indigo-400/30">
              <span className="text-white text-sm font-semibold">
                {user?.displayName?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">
                {user?.displayName || 'Gebruiker'}
              </p>
              <p className="text-slate-400 text-xs truncate">{user?.email || ''}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-all duration-200"
              title="Uitloggen"
            >
              <LogoutIcon />
            </button>
          </div>
        </div>
      </aside>

      {sidebarCollapsed && (
        <button
          type="button"
          onClick={() => setSidebarCollapsed(false)}
          title="Menu uitklappen (⌘\)"
          className="w-4 flex-shrink-0 bg-slate-900 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition-colors"
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        </button>
      )}

      {/* Main content. Het extern loket komt op elke andere URL (ook een
          ingetypte) op zijn eigen startscherm uit; de RoleGuards per route zijn
          daarnaast alleen nog documentatie. */}
      <main className="flex-1 overflow-hidden flex flex-col min-w-0">
        <AppUpdateBanner />
        {rolOnbekend && (
          <div className="flex items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900" role="alert">
            <span>Je rol kon niet geladen worden, daarom is het menu leeg. Controleer je verbinding en probeer opnieuw.</span>
            <span className="flex shrink-0 gap-2">
              <button type="button" onClick={herlaadRol} disabled={rolLaadt} className="rounded border border-amber-300 bg-white px-2 py-1 text-amber-900 hover:bg-amber-100 disabled:opacity-50">
                {rolLaadt ? 'Bezig…' : 'Opnieuw proberen'}
              </button>
              <button type="button" onClick={() => window.location.reload()} className="rounded bg-amber-600 px-2 py-1 text-white hover:bg-amber-700">
                Opnieuw laden
              </button>
            </span>
          </div>
        )}
        {isExternLoket(role) && !isExternLoketPath(location.pathname)
          ? <Navigate to={EXTERN_LOKET_HOME} replace />
          : <Outlet />}
      </main>

      {/* Docked softphone (slide-in variant). The sidebar variant is rendered
          inside <aside> above the user block; PHONE_DOCK_VARIANT picks one. */}
      {role !== 'callcenter' && PHONE_DOCK_VARIANT === 'slide-in' && <SlideInPhonePanel />}

      {/* Global appointment scheduler side-sheet, triggered via store */}
      <AppointmentScheduler />

      {/* Global offerte accept panel, triggered via store */}
      <OffertePanel />

      {/* Global command palette */}
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />

      {/* Global wasserij-item drawer, triggered via store */}
      <WasserijItemDrawerPanel />

      {/* Global offerte-detail drawer, triggered via store — vóór de
          order-drawer zodat die bij gelijk z-index bovenop paint */}
      <OfferteDetailDrawerPanel />

      {/* Global order-detail drawer, triggered via store */}
      <OrderDetailDrawerPanel />

      {/* Global opportunity-detail drawer (Cinco-platform doorverwijzingen) */}
      <OpportunityDetailDrawerPanel />

      {/* Global call-detail drawer, triggered via store */}
      <CallDetailDrawerPanel />

      {/* Global invoice-detail drawer, triggered via store */}
      <InvoiceDetailDrawerPanel />
    </div>
  );
}
